export interface Brand {
	name: string;
	logo: string;
	fromEmail: string;
	supportEmail: string;
	portalUrl: string;
}

const BRANDS: Record<string, Brand> = {
	"govassist.com": {
		name: "GovAssist",
		logo: "/logo/govassist.png",
		fromEmail: "noreply@govassist.com",
		supportEmail: "support@govassist.com",
		portalUrl: "https://portal.govassist.com",
	},
	"quickpassport.com": {
		name: "QuickPassport",
		logo: "/logo/quickpassport.png",
		fromEmail: "noreply@quickpassport.com",
		supportEmail: "support@quickpassport.com",
		portalUrl: "https://portal.quickpassport.com",
	},
	"ivs.us": {
		name: "International Visa Services",
		logo: "/logo/ivs.png",
		fromEmail: "noreply@ivs.us",
		supportEmail: "support@ivs.us",
		portalUrl: "https://portal.ivs.us",
	},
	"portal.entry.com": {
		name: "Entry",
		logo: "/logo/entry.png",
		fromEmail: "noreply@entry.com",
		supportEmail: "support@entry.com",
		portalUrl: "https://portal.entry.com",
	},
};

export const DEFAULT_BRAND: Brand = BRANDS["govassist.com"];

export function getBrandFromHost(host: string): Brand {
	const hostname = host.split(":")[0].toLowerCase();
	return BRANDS[hostname] ?? DEFAULT_BRAND;
}

export function getBrandFromWindow(): Brand {
	if (typeof window === "undefined") return DEFAULT_BRAND;
	return BRANDS[window.location.hostname] ?? DEFAULT_BRAND;
}

export const BRAND_HOSTNAMES = Object.keys(BRANDS);

export function getBrandByKey(key: string): Brand | null {
	return BRANDS[key] ?? null;
}

const SIGN_IN_PATH = "/auth/sign-in";

const LOCAL_HOSTNAMES = new Set(["localhost", "127.0.0.1", "0.0.0.0", "[::1]"]);

function isLocalHost(host: string): boolean {
	const hostname = host.split(":")[0].toLowerCase();
	return LOCAL_HOSTNAMES.has(hostname);
}

function getRequestOrigin(request: Request): string {
	const host = request.headers.get("host") ?? "";
	if (!host) return "";
	const protoHeader = request.headers.get("x-forwarded-proto");
	const proto = getRequestProto(protoHeader, host);
	return `${proto}://${host}`;
}

function getRequestProto(protoHeader: string | null, host: string): string {
	if (protoHeader) return protoHeader.split(",")[0].trim();
	return isLocalHost(host) ? "http" : "https";
}

/**
 * Resolves the brand and sign-in URL to use for a request.
 *
 * Resolution order:
 *   1. If `bodyBrand` is provided, it MUST match a BRANDS key. Unknown keys
 *      throw — callers should gate with Zod first.
 *   2. Otherwise resolve via the Host header.
 *
 * Sign-in URL base:
 *   - Local dev requests (localhost, 127.0.0.1, etc.) always use the request
 *     origin, so the CTA link points back to the same dev deployment where
 *     the account was created — regardless of whether `bodyBrand` was set.
 *   - All other requests use the brand's canonical `portalUrl`, so cross-brand
 *     admin calls land on the correct production host even when made from a
 *     different origin.
 */
export function resolveBrand(
	request: Request,
	bodyBrand?: string,
): { brand: Brand; signInUrl: string } {
	const host = request.headers.get("host") ?? "";
	const origin = getRequestOrigin(request);
	const useRequestOrigin = origin !== "" && isLocalHost(host);

	if (bodyBrand) {
		const brand = getBrandByKey(bodyBrand);
		if (!brand) {
			throw new Error(`Unknown brand key: ${bodyBrand}`);
		}
		const base = useRequestOrigin ? origin : brand.portalUrl;
		return { brand, signInUrl: `${base}${SIGN_IN_PATH}` };
	}

	const brand = getBrandFromHost(host);
	const base = origin || brand.portalUrl;
	return { brand, signInUrl: `${base}${SIGN_IN_PATH}` };
}
